// src/pages/CookiesPolicy.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalTradeName: "Académie ZEDRO",
  websiteLabel: "zedro.academy",
  privacyEmail: "admin@zedro.academy",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const COOKIES_COPY = {
  es: {
    title: "Política de Cookies",
    updated: "Última actualización: 17 de julio de 2026",
    intro: [
      <>
        Esta Política explica cómo <strong>{OPERATOR.platformName}</strong>,
        plataforma operada por <strong>{OPERATOR.legalTradeName}</strong>, utiliza
        cookies, almacenamiento local y tecnologías similares en{" "}
        <strong>{OPERATOR.websiteLabel}</strong>.
      </>,
      <>
        Esta Política complementa la{" "}
        <Link to="/legal/privacidad">Política de Privacidad</Link>. Las
        tecnologías no esenciales permanecen desactivadas hasta que la persona
        expresa su elección mediante el banner o el panel de preferencias.
      </>,
    ],
    sections: [
      {
        title: "1. Qué son las cookies y tecnologías similares",
        paragraphs: [
          <>
            Las cookies son pequeños archivos que un sitio web o un proveedor
            externo puede guardar en el navegador o dispositivo. También podemos
            utilizar almacenamiento local, identificadores técnicos, píxeles,
            etiquetas y herramientas similares para mantener sesiones, recordar
            preferencias, proteger transacciones y medir el uso autorizado del
            sitio.
          </>,
        ],
      },
      {
        title: "2. Categorías utilizadas",
        paragraphs: [
          <>Las tecnologías utilizadas se agrupan en estas categorías:</>,
        ],
        items: [
          {
            label: "Esenciales y de seguridad",
            text:
              "Permiten operar el sitio, mantener funciones de cuenta y sesión, proteger formularios y accesos, conservar la elección de privacidad y prevenir fraude. No pueden desactivarse desde el panel cuando son estrictamente necesarias para prestar el servicio solicitado.",
          },
          {
            label: "Preferencias",
            text:
              "Recuerdan configuraciones elegidas por la persona, como el idioma de la plataforma y las preferencias de cookies.",
          },
          {
            label: "Analítica",
            text:
              "Permiten comprender de forma agregada cómo se utiliza el sitio, medir visitas y rendimiento y detectar oportunidades de mejora. Actualmente utilizamos Google Analytics 4, cargado mediante Google Tag Manager, únicamente después de recibir consentimiento.",
          },
          {
            label: "Pago y prevención de fraude",
            text:
              "Stripe puede utilizar cookies y tecnologías similares durante el proceso de pago para procesar la transacción, autenticar al cliente, detectar dispositivos y prevenir fraude.",
          },
        ],
      },
      {
        title: "3. Tecnologías principales",
        paragraphs: [
          <>Las tecnologías actualmente relevantes pueden incluir:</>,
        ],
        items: [
          {
            label: "Preferencia de idioma",
            text:
              "El almacenamiento local identificado como zedro_academy_language recuerda el idioma seleccionado. Permanece hasta que la persona cambia la preferencia, elimina los datos del navegador o restablece el almacenamiento del sitio.",
          },
          {
            label: "Preferencia de consentimiento",
            text:
              "Una señal técnica conserva las categorías aceptadas o rechazadas para no solicitar la misma elección en cada visita. Puede renovarse cuando cambien las prácticas, categorías o proveedores.",
          },
          {
            label: "Google Analytics 4",
            text:
              "Cuando se autoriza la analítica, Google Analytics 4 puede utilizar las cookies _ga y _ga_<identificador> para distinguir visitas y conservar el estado de la sesión. Su duración predeterminada puede alcanzar dos años, salvo que se configure un periodo menor o la persona las elimine.",
          },
          {
            label: "Stripe",
            text:
              "En páginas o flujos de pago, Stripe puede utilizar identificadores como __stripe_mid, __stripe_sid, m u otros equivalentes para funcionamiento, autenticación y prevención de fraude. Stripe determina su duración de acuerdo con la función y sus propias políticas.",
          },
        ],
        after: [
          <>
            Los nombres técnicos pueden cambiar cuando un proveedor actualiza su
            servicio, siempre que la finalidad y la categoría correspondientes
            continúen informándose y se respete la elección de la persona.
          </>,
        ],
      },
      {
        title: "4. Finalidades",
        paragraphs: [
          <>
            Utilizamos estas tecnologías únicamente para las finalidades
            correspondientes a la categoría elegida:
          </>,
        ],
        bullets: [
          "operar, proteger y mantener la plataforma;",
          "permitir funciones de cuenta, sesión y acceso;",
          "recordar idioma y preferencias de privacidad;",
          "procesar pagos y prevenir transacciones fraudulentas;",
          "medir uso y rendimiento cuando la analítica ha sido autorizada;",
          "detectar errores técnicos y mejorar la experiencia.",
        ],
      },
      {
        title: "5. Consentimiento y configuración predeterminada",
        paragraphs: [
          <>
            Las tecnologías estrictamente necesarias pueden utilizarse sin
            consentimiento cuando son indispensables para prestar una función
            solicitada, proteger el servicio o conservar la elección de
            privacidad.
          </>,
          <>
            Las tecnologías de analítica y cualquier futura tecnología de
            publicidad, segmentación, identificación, localización o perfilado
            permanecen desactivadas de manera predeterminada y solo se activan
            después de una elección afirmativa, cuando la ley exige
            consentimiento.
          </>,
        ],
      },
      {
        title: "6. Aceptar, rechazar o modificar preferencias",
        paragraphs: [
          <>
            Desde el banner o panel de cookies puedes aceptar las tecnologías no
            esenciales, rechazarlas o seleccionar únicamente algunas categorías.
            Rechazarlas no impide el acceso a las páginas públicas ni a las
            funciones que no dependan de ellas.
          </>,
          <>
            Puedes retirar o modificar tu consentimiento posteriormente desde el
            control de “Preferencias de cookies” disponible en el sitio. El
            cambio se aplica a los tratamientos futuros y no afecta la licitud
            del uso realizado antes del retiro.
          </>,
        ],
      },
      {
        title: "7. Configuración del navegador",
        paragraphs: [
          <>
            La mayoría de los navegadores permiten ver, bloquear o eliminar
            cookies y almacenamiento local. La eliminación de tecnologías
            esenciales puede cerrar una sesión, borrar preferencias o impedir el
            funcionamiento correcto de determinadas áreas de la plataforma.
          </>,
        ],
      },
      {
        title: "8. Proveedores externos",
        paragraphs: [
          <>
            Google y Stripe pueden recibir información técnica cuando sus
            servicios autorizados o necesarios son utilizados. Esos proveedores
            tratan la información conforme a sus propias políticas y pueden
            procesarla fuera de Québec o Canadá.
          </>,
          <>
            La información sobre transferencias y tratamiento de datos
            personales se encuentra en la{" "}
            <Link to="/legal/privacidad">Política de Privacidad</Link>.
          </>,
        ],
      },
      {
        title: "9. Publicidad y segmentación",
        paragraphs: [
          <>
            Actualmente {OPERATOR.platformName} no utiliza cookies de publicidad
            comportamental ni de segmentación publicitaria en su sitio. Si estas
            tecnologías se incorporan en el futuro, se actualizará esta Política
            y se solicitará la elección correspondiente antes de activarlas.
          </>,
        ],
      },
      {
        title: "10. Conservación",
        paragraphs: [
          <>
            La duración depende de la finalidad y del proveedor. Algunas
            tecnologías duran únicamente durante la sesión; otras permanecen
            hasta que expira el periodo configurado, se modifica la preferencia o
            la persona elimina los datos del navegador.
          </>,
          <>
            Procuramos conservar las preferencias y los identificadores solo
            durante el tiempo necesario para la finalidad informada.
          </>,
        ],
      },
      {
        title: "11. Actualizaciones",
        paragraphs: [
          <>
            Podemos actualizar esta Política cuando cambien las tecnologías, los
            proveedores, las finalidades o las obligaciones legales. La versión
            vigente es la publicada en el sitio con su fecha de actualización.
          </>,
          <>
            Cuando un cambio requiera una nueva elección, las tecnologías
            afectadas no se activarán hasta que la persona actualice sus
            preferencias.
          </>,
        ],
      },
      {
        title: "12. Contacto",
        paragraphs: [
          <>
            Para preguntas sobre cookies, consentimiento o privacidad, escribe a{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>
              {OPERATOR.privacyEmail}
            </a>
            .
          </>,
        ],
      },
      {
        title: "13. Documentos relacionados",
        paragraphs: [
          <>
            Esta Política complementa los{" "}
            <Link to="/legal/terminos">
              Términos y Condiciones de Uso, Compra y Suscripción
            </Link>
            , la <Link to="/legal/privacidad">Política de Privacidad</Link>, el{" "}
            <Link to="/legal/aviso-legal">Aviso legal</Link> y el{" "}
            <Link to="/legal/descargo">Descargo de responsabilidad</Link>.
          </>,
        ],
      },
    ],
  },

  fr: {
    title: "Politique relative aux témoins",
    updated: "Dernière mise à jour : 17 juillet 2026",
    intro: [
      <>
        La présente Politique explique comment{" "}
        <strong>{OPERATOR.platformName}</strong>, une plateforme exploitée par{" "}
        <strong>{OPERATOR.legalTradeName}</strong>, utilise des témoins, le
        stockage local et des technologies similaires sur{" "}
        <strong>{OPERATOR.websiteLabel}</strong>.
      </>,
      <>
        Elle complète la{" "}
        <Link to="/legal/privacidad">Politique de confidentialité</Link>. Les
        technologies non essentielles demeurent désactivées jusqu’à ce que la
        personne exprime son choix au moyen de la bannière ou du panneau de
        préférences.
      </>,
    ],
    sections: [
      {
        title: "1. Témoins et technologies similaires",
        paragraphs: [
          <>
            Les témoins sont de petits fichiers qu’un site Web ou un fournisseur
            externe peut enregistrer dans le navigateur ou l’appareil. Nous
            pouvons également utiliser le stockage local, des identifiants
            techniques, des pixels, des balises et des outils similaires afin de
            maintenir des sessions, mémoriser des préférences, protéger les
            transactions et mesurer l’utilisation autorisée du site.
          </>,
        ],
      },
      {
        title: "2. Catégories utilisées",
        paragraphs: [
          <>
            Les technologies utilisées sont regroupées dans les catégories
            suivantes :
          </>,
        ],
        items: [
          {
            label: "Essentiels et sécurité",
            text:
              "Ils permettent de faire fonctionner le site, de maintenir les fonctions de compte et de session, de protéger les formulaires et les accès, de conserver le choix relatif à la vie privée et de prévenir la fraude. Ils ne peuvent pas être désactivés dans le panneau lorsqu’ils sont strictement nécessaires au service demandé.",
          },
          {
            label: "Préférences",
            text:
              "Ils mémorisent les paramètres choisis par la personne, notamment la langue de la plateforme et les préférences relatives aux témoins.",
          },
          {
            label: "Analytique",
            text:
              "Ils permettent de comprendre de façon agrégée l’utilisation du site, de mesurer les visites et le rendement et de repérer des améliorations possibles. Nous utilisons actuellement Google Analytics 4, chargé au moyen de Google Tag Manager, uniquement après l’obtention du consentement.",
          },
          {
            label: "Paiement et prévention de la fraude",
            text:
              "Stripe peut utiliser des témoins et des technologies similaires pendant le processus de paiement afin de traiter la transaction, d’authentifier le client, de détecter les appareils et de prévenir la fraude.",
          },
        ],
      },
      {
        title: "3. Principales technologies",
        paragraphs: [
          <>Les technologies actuellement pertinentes peuvent comprendre :</>,
        ],
        items: [
          {
            label: "Préférence de langue",
            text:
              "Le stockage local identifié comme zedro_academy_language mémorise la langue choisie. Il demeure jusqu’à ce que la personne modifie sa préférence, supprime les données du navigateur ou réinitialise le stockage du site.",
          },
          {
            label: "Préférence de consentement",
            text:
              "Un signal technique conserve les catégories acceptées ou refusées afin d’éviter de demander le même choix à chaque visite. Il peut être renouvelé lorsque les pratiques, catégories ou fournisseurs changent.",
          },
          {
            label: "Google Analytics 4",
            text:
              "Lorsque l’analytique est autorisée, Google Analytics 4 peut utiliser les témoins _ga et _ga_<identifiant> afin de distinguer les visites et de conserver l’état de la session. Leur durée par défaut peut atteindre deux ans, sauf si une durée plus courte est configurée ou si la personne les supprime.",
          },
          {
            label: "Stripe",
            text:
              "Dans les pages ou parcours de paiement, Stripe peut utiliser des identifiants comme __stripe_mid, __stripe_sid, m ou d’autres équivalents pour le fonctionnement, l’authentification et la prévention de la fraude. Stripe en détermine la durée selon leur fonction et ses propres politiques.",
          },
        ],
        after: [
          <>
            Les noms techniques peuvent changer lorsqu’un fournisseur met son
            service à jour, pourvu que la finalité et la catégorie
            correspondantes demeurent indiquées et que le choix de la personne
            soit respecté.
          </>,
        ],
      },
      {
        title: "4. Finalités",
        paragraphs: [
          <>
            Nous utilisons ces technologies uniquement aux fins correspondant à
            la catégorie choisie :
          </>,
        ],
        bullets: [
          "faire fonctionner, protéger et maintenir la plateforme;",
          "permettre les fonctions de compte, de session et d’accès;",
          "mémoriser la langue et les préférences relatives à la vie privée;",
          "traiter les paiements et prévenir les transactions frauduleuses;",
          "mesurer l’utilisation et le rendement lorsque l’analytique est autorisée;",
          "détecter les erreurs techniques et améliorer l’expérience.",
        ],
      },
      {
        title: "5. Consentement et paramètres par défaut",
        paragraphs: [
          <>
            Les technologies strictement nécessaires peuvent être utilisées sans
            consentement lorsqu’elles sont indispensables à la prestation d’une
            fonction demandée, à la protection du service ou à la conservation
            du choix relatif à la vie privée.
          </>,
          <>
            Les technologies d’analytique ainsi que toute future technologie de
            publicité, de ciblage, d’identification, de localisation ou de
            profilage demeurent désactivées par défaut et ne sont activées
            qu’après un choix affirmatif lorsque la loi exige un consentement.
          </>,
        ],
      },
      {
        title: "6. Accepter, refuser ou modifier les préférences",
        paragraphs: [
          <>
            La bannière ou le panneau de témoins permet d’accepter les
            technologies non essentielles, de les refuser ou de sélectionner
            certaines catégories seulement. Le refus n’empêche pas l’accès aux
            pages publiques ni aux fonctions qui n’en dépendent pas.
          </>,
          <>
            Vous pouvez retirer ou modifier votre consentement par la suite au
            moyen du contrôle « Préférences relatives aux témoins » disponible
            sur le site. La modification s’applique aux traitements futurs et
            n’affecte pas la licéité de l’utilisation effectuée avant le retrait.
          </>,
        ],
      },
      {
        title: "7. Paramètres du navigateur",
        paragraphs: [
          <>
            La plupart des navigateurs permettent d’afficher, de bloquer ou de
            supprimer les témoins et le stockage local. La suppression de
            technologies essentielles peut fermer une session, effacer des
            préférences ou empêcher le bon fonctionnement de certaines parties
            de la plateforme.
          </>,
        ],
      },
      {
        title: "8. Fournisseurs externes",
        paragraphs: [
          <>
            Google et Stripe peuvent recevoir des renseignements techniques
            lorsque leurs services autorisés ou nécessaires sont utilisés. Ces
            fournisseurs traitent les renseignements conformément à leurs
            propres politiques et peuvent les traiter hors Québec ou hors
            Canada.
          </>,
          <>
            Les renseignements sur les transferts et le traitement des
            renseignements personnels se trouvent dans la{" "}
            <Link to="/legal/privacidad">Politique de confidentialité</Link>.
          </>,
        ],
      },
      {
        title: "9. Publicité et ciblage",
        paragraphs: [
          <>
            À l’heure actuelle, {OPERATOR.platformName} n’utilise pas de témoins
            de publicité comportementale ni de ciblage publicitaire sur son
            site. Si de telles technologies sont ajoutées, la présente Politique
            sera mise à jour et le choix requis sera demandé avant leur
            activation.
          </>,
        ],
      },
      {
        title: "10. Conservation",
        paragraphs: [
          <>
            La durée varie selon la finalité et le fournisseur. Certaines
            technologies ne durent que pendant la session; d’autres demeurent
            jusqu’à l’expiration de la période configurée, à la modification de
            la préférence ou à la suppression des données du navigateur par la
            personne.
          </>,
          <>
            Nous nous efforçons de conserver les préférences et les identifiants
            uniquement pendant la période nécessaire à la finalité indiquée.
          </>,
        ],
      },
      {
        title: "11. Mises à jour",
        paragraphs: [
          <>
            Nous pouvons mettre à jour la présente Politique lorsque les
            technologies, les fournisseurs, les finalités ou les obligations
            légales changent. La version en vigueur est celle publiée sur le site
            avec sa date de mise à jour.
          </>,
          <>
            Lorsqu’un changement exige un nouveau choix, les technologies
            concernées ne sont pas activées avant que la personne mette ses
            préférences à jour.
          </>,
        ],
      },
      {
        title: "12. Coordonnées",
        paragraphs: [
          <>
            Pour toute question concernant les témoins, le consentement ou la vie
            privée, écrivez à{" "}
            <a href={`mailto:${OPERATOR.privacyEmail}`}>
              {OPERATOR.privacyEmail}
            </a>
            .
          </>,
        ],
      },
      {
        title: "13. Documents connexes",
        paragraphs: [
          <>
            La présente Politique complète les{" "}
            <Link to="/legal/terminos">
              Conditions d’utilisation, d’achat et d’abonnement
            </Link>
            , la <Link to="/legal/privacidad">Politique de confidentialité</Link>,
            l’<Link to="/legal/aviso-legal">Avis juridique</Link> et l’{" "}
            <Link to="/legal/descargo">Avis de non-responsabilité</Link>.
          </>,
        ],
      },
    ],
  },
};

function renderParagraphs(paragraphs = []) {
  return paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>);
}

function renderItems(items = []) {
  if (!items.length) return null;

  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>
          <strong>{item.label}:</strong> {item.text}
        </li>
      ))}
    </ul>
  );
}

function renderBullets(items = []) {
  if (!items.length) return null;

  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export default function CookiesPolicy() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = COOKIES_COPY[language] || COOKIES_COPY.es;

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p
        style={{
          color: "rgba(44, 59, 81, 0.72)",
          fontSize: "0.95rem",
          marginTop: "-0.4rem",
        }}
      >
        {copy.updated}
      </p>

      {renderParagraphs(copy.intro)}

      {copy.sections.map((section) => (
        <section key={section.title} style={{ marginTop: "1.5rem" }}>
          <h2
            style={{
              color: "#264763",
              fontSize: "1.2rem",
              marginBottom: "0.45rem",
            }}
          >
            {section.title}
          </h2>

          {renderParagraphs(section.paragraphs)}
          {renderItems(section.items)}
          {renderBullets(section.bullets)}
          {renderParagraphs(section.after)}
        </section>
      ))}
    </main>
  );
}
